import { format } from 'date-fns';
import ko from 'date-fns/locale/ko';
import { t } from 'i18next';
import { findIndex, isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import StickyHeader from '../components/StickyHeader';
import VerifiedIcon from '../components/icons/VerifiedIcon';
import { ellipsis } from '../utils/TextUtil';
import { refreshScreenSize } from '../utils/UIUtils';
import { queryGet } from '../utils/queryClient';

interface DMUserProfile {
  userId: string;
  userProfileId: string;
  nickname: string;
  identified: boolean;
  thumbnailUrl?: string;
  pictureUrl?: string;
}

interface DMLastMessage {
  chatRoomMessageId: string;
  content: string;
  sentAt: string;
  messageType: string;
  isMine: boolean;
}

interface DMChatRoom {
  chatRoomId: string;
  currentUserId: string;
  userProfiles: DMUserProfile[];
  lastMessage?: DMLastMessage;
  unreadCount: number;
  updatedAt: string;
}

const POLLING_INTERVAL = 60000; // 1분마다 폴링

export default function DMListPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPollingActive, setIsPollingActive] = useState(true);

  useEffect(() => {
    refreshScreenSize();
    return () => {
      // 컴포넌트 언마운트 시 interval cleanup
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsPollingActive(false);
    };
  }, []);

  // TODO : API V1 으로 수정
  const { data: chatRooms, refetch, isLoading } = useQuery(
    ['chatRooms'],
    () => queryGet('/chat_rooms'),
    {
      enabled: true,
      select: (r: any) => r.chatRooms as DMChatRoom[],
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );

  // 주기적 폴링 설정
  useEffect(() => {
    if (!isPollingActive) return;

    const startPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        if (isPollingActive && !document.hidden) {
          refetch();
        }
      }, POLLING_INTERVAL);
    };

    // 페이지가 보이는 상태일 때만 폴링
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else if (isPollingActive) {
        refetch();
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    startPolling();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [refetch, isPollingActive]);

  // TODO : DM UserProfile 을 정확히 찾지 못함.
  const getDmUserProfile = (chatRoom: DMChatRoom): DMUserProfile | undefined => {
    if (!chatRoom.userProfiles) return undefined;
    const index = findIndex(chatRoom.userProfiles, (p) => p.userId !== chatRoom.currentUserId);
    return chatRoom.userProfiles[index];
  };

  const formatLastMessageTime = (sentAt: string): string => {
    try {
      const messageTime = new Date(sentAt);
      const now = new Date();
      const diffInMs = now.getTime() - messageTime.getTime();
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInDays === 0) {
        return format(messageTime, 'HH:mm', { locale: ko });
      } else if (diffInDays < 7) {
        return format(messageTime, 'EEEE', { locale: ko });
      } else {
        return format(messageTime, 'MM/dd', { locale: ko });
      }
    } catch (error) {
      return '';
    }
  };

  const getLastMessageContent = (lastMessage?: DMLastMessage): string => {
    if (!lastMessage) return '';

    if (lastMessage.messageType === 'image') {
      return t('DMList.imageMessage');
    }

    return ellipsis(lastMessage.content, 50);
  };

  const navigateToChatRoom = (chatRoomId: string) => {
    // 채팅방으로 이동할 때 폴링 중단
    setIsPollingActive(false);
    navigate(`/chat_rooms/${chatRoomId}/v2`);
  };

  const handleRefresh = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div>
        <StickyHeader
          title={String(t('DMList.title'))}
          showTitle={true}
          alwaysShowTitle={true}
          showBackButton={true}
          transparent={true}
        />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-light dark:border-primary-dark"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <StickyHeader
        title={String(t('DMList.title'))}
        showTitle={true}
        showBackButton={true}
        alwaysShowTitle={true}
        transparent={false}
      />

      <div className="pt-[56px] px-4 py-2">
        {isEmpty(chatRooms) ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="text-gray-400 dark:text-gray-500 mb-2">
              <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V5zM2 11a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2z" />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-600 dark:text-gray-300">
              {t('DMList.emptyChatRooms')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('DMList.emptyChatRoomsDescription')}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {chatRooms?.map((chatRoom) => {
              const dmUserProfile = getDmUserProfile(chatRoom);

              return (
                <div
                  key={chatRoom.chatRoomId}
                  className="flex items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                  onClick={() => navigateToChatRoom(chatRoom.chatRoomId)}
                >
                  {/* 프로필 이미지 */}
                  <div className="relative mr-3">
                    <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                      {dmUserProfile?.thumbnailUrl || dmUserProfile?.pictureUrl ? (
                        <img
                          src={dmUserProfile.thumbnailUrl || dmUserProfile.pictureUrl}
                          alt={dmUserProfile.nickname}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    {chatRoom.unreadCount > 0 && (
                      <div className="absolute -top-1 -right-1 min-w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-medium px-1">
                          {chatRoom.unreadCount > 99 ? '99+' : chatRoom.unreadCount}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* 채팅 정보 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {dmUserProfile?.nickname || t('DMList.unknownUser')}
                        </h3>
                        {dmUserProfile?.identified && (
                          <VerifiedIcon width="12px" height="12px" className="ml-1 flex-shrink-0" />
                        )}
                      </div>
                      {chatRoom.lastMessage && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {formatLastMessageTime(chatRoom.lastMessage.sentAt)}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center">
                      <p
                        className={`text-sm truncate ${
                          chatRoom.unreadCount > 0
                            ? 'text-gray-900 dark:text-gray-100 font-medium'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {getLastMessageContent(chatRoom.lastMessage) || t('DMList.noMessages')}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
